import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { usePostHog } from 'posthog-js/react';
import { useTheme } from '../contexts/ThemeContext';
import VerticalTimeline from '../components/VerticalTimeline';
import FilterSection from '../components/FilterSection';
import PredictionModal from '../components/PredictionModal';
import DatasetToggle from '../components/DatasetToggle';
import { getPredictionsDataPath } from '../utils/dataParser';
import { 
  CACHE_VALIDITY_HOURS, 
  PREDICTIONS_CACHE_KEY, 
  PREDICTIONS_CACHE_TIMESTAMP_KEY,
  TRACKING_EVENTS,
  ENGAGEMENT_TRACKING_INTERVAL 
} from '../constants';
import { getCachedPredictions, cachePredictions } from '../utils/localStorage';
import { parseTagsOrAuthors } from '../utils/dataParser';

const TimelineContainer = styled.div`
  padding-top: 100px;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding-top: 80px;
  }
`;

const Header = styled.header`
  text-align: center;
  padding: 5rem 2rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 2rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  color: ${props => props.theme.colors.text};
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const MainContent = styled.main`
  width: 100%;
`;

const TimelineLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  color: ${props => props.theme.colors.textSecondary};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${props => props.theme.colors.borderLight};
  border-top: 3px solid ${props => props.theme.colors.text};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #ff4444;
  font-size: 1.1rem;
`;

function TimelinePage() {
  const { themeData } = useTheme();
  const posthog = usePostHog();
  const [predictions, setPredictions] = useState([]);
  const [selectedTags, setSelectedTags] = useState(['All']);
  const [selectedAuthors, setSelectedAuthors] = useState(['All']);
  const [yearRange, setYearRange] = useState({ min: null, max: null });
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrivateDataset, setIsPrivateDataset] = useState(false);
  const sessionStartTime = useRef(Date.now());

  // Simple CSV line parser that handles quoted fields
  const parseCSVLine = useCallback((line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result;
  }, []);

  // Enhanced CSV parser that handles multi-line quoted fields
  const parseCSV = useCallback((csvText) => {
    const lines = csvText.split('\n');
    const result = [];
    let currentRow = '';
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      if (currentRow) {
        currentRow += '\n' + line;
      } else {
        currentRow = line;
      }
      
      let quoteCount = 0;
      for (let j = 0; j < currentRow.length; j++) {
        if (currentRow[j] === '"') quoteCount++;
      }
      
      if (quoteCount % 2 === 0) {
        result.push(parseCSVLine(currentRow));
        currentRow = '';
      }
    }
    
    if (currentRow) {
      result.push(parseCSVLine(currentRow));
    }
    
    return result;
  }, [parseCSVLine]);

  // Track page view and session start
  useEffect(() => {
    posthog?.capture(TRACKING_EVENTS.PAGE_VIEW, {
      page_title: 'Timeline - Singularity Countdown'
    });
    
    posthog?.capture(TRACKING_EVENTS.SESSION_STARTED, {
      page_title: 'Timeline - Singularity Countdown',
      timestamp: new Date().toISOString()
    });
    
    if (posthog && !posthog.get_distinct_id()) {
      posthog.identify();
    }
  }, [posthog]);

  // Track session end
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!posthog) return;
      
      const sessionDuration = Date.now() - sessionStartTime.current;
      posthog.capture(TRACKING_EVENTS.SESSION_ENDED, {
        session_duration_ms: sessionDuration,
        session_duration_minutes: Math.round(sessionDuration / 60000 * 100) / 100,
        predictions_loaded_count: predictions.length,
        filters_used: selectedTags.length > 1 || selectedAuthors.length > 1,
        predictions_viewed: selectedPrediction ? 1 : 0
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [posthog, predictions.length, selectedTags.length, selectedAuthors.length, selectedPrediction]);

  // Track user engagement
  useEffect(() => {
    if (!posthog) return;

    const trackEngagement = () => {
      const sessionDuration = Date.now() - sessionStartTime.current;
      const yearRangeSpan = (yearRange.min !== null && yearRange.max !== null) 
        ? yearRange.max - yearRange.min 
        : 0;
      
      posthog.capture(TRACKING_EVENTS.USER_ENGAGED, {
        session_duration_ms: sessionDuration,
        predictions_count: predictions.length,
        active_filters: selectedTags.filter(tag => tag !== 'All').length + selectedAuthors.filter(author => author !== 'All').length,
        current_year_range: yearRangeSpan
      });
    };

    const interval = setInterval(trackEngagement, ENGAGEMENT_TRACKING_INTERVAL);
    return () => clearInterval(interval);
  }, [posthog, predictions.length, selectedTags, selectedAuthors, yearRange]);

  // Load data from CSV file with localStorage caching
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Use different cache keys for private vs public datasets
        const cacheKey = isPrivateDataset 
          ? `${PREDICTIONS_CACHE_KEY}_private` 
          : `${PREDICTIONS_CACHE_KEY}_public`;
        const timestampKey = isPrivateDataset 
          ? `${PREDICTIONS_CACHE_TIMESTAMP_KEY}_private` 
          : `${PREDICTIONS_CACHE_TIMESTAMP_KEY}_public`;
        
        const cachedData = getCachedPredictions(
          cacheKey,
          timestampKey,
          CACHE_VALIDITY_HOURS
        );
        
        if (cachedData) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Loading predictions from cache (${isPrivateDataset ? 'private' : 'public'})`);
          }
          setPredictions(cachedData);
          setIsLoading(false);
          posthog?.capture(TRACKING_EVENTS.PREDICTIONS_LOADED_CACHE, { 
            prediction_count: cachedData.length,
            dataset_type: isPrivateDataset ? 'private' : 'public'
          });
          return;
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`Fetching predictions from server (${isPrivateDataset ? 'private' : 'public'})`);
        }
        const dataPath = getPredictionsDataPath(isPrivateDataset);
        const response = await fetch(dataPath);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const csvText = await response.text();
        
        const allRows = parseCSV(csvText);
        if (allRows.length === 0) {
          throw new Error('Empty CSV file');
        }
        
        const headers = allRows[0].map(h => h.trim());
        const parsedData = [];
        
        for (let i = 1; i < allRows.length; i++) {
          const values = allRows[i];
          if (values && values.length > 0) {
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] ? values[index].trim().replace(/^"|"$/g, '') : '';
            });
            
            if (row.id && row.display_year && row.title) {
              parsedData.push({
                id: parseInt(row.id, 10),
                display_year: parseInt(row.display_year, 10),
                timeframe: row.timeframe || '',
                title: row.title,
                text: row.rewritten || row.text || '',
                tags: row.tags || '',
                image: row.image || '/images/placeholder.svg',
                source: row.source || '',
                author: row.author || '',
                date: row.date || '',
                link: row.link || '',
                realized: row.realized || '',
                realized_date: row.realized_date || '',
                realized_links: row.realized_links || '',
                raw_text: row.raw_text || ''
              });
            }
          }
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Loaded predictions:', parsedData.length);
        }
        
        const cacheSuccess = cachePredictions(
          cacheKey,
          timestampKey,
          parsedData
        );
        
        if (process.env.NODE_ENV === 'development' && cacheSuccess) {
          console.log(`Predictions cached to localStorage (${isPrivateDataset ? 'private' : 'public'})`);
        }
        
        setPredictions(parsedData);
        posthog?.capture(TRACKING_EVENTS.PREDICTIONS_LOADED_SERVER, { 
          prediction_count: parsedData.length,
          dataset_type: isPrivateDataset ? 'private' : 'public'
        });
      } catch (error) {
        console.error('Error loading CSV data:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [parseCSV, posthog, isPrivateDataset]);

  // Get unique tags from all predictions
  const allTags = useMemo(() => {
    const tagSet = new Set(['All']);
    predictions.forEach(prediction => {
      if (prediction.tags) {
        const tagList = parseTagsOrAuthors(prediction.tags);
        tagList.forEach(tag => {
          const trimmedTag = tag.trim();
          if (trimmedTag) {
            tagSet.add(trimmedTag);
          }
        });
      }
    });
    return Array.from(tagSet);
  }, [predictions]);

  // Get unique authors from all predictions
  const allAuthors = useMemo(() => {
    const authorSet = new Set();
    predictions.forEach(prediction => {
      if (prediction.author) {
        const authorList = parseTagsOrAuthors(prediction.author);
        authorList.forEach(author => {
          const trimmedAuthor = author.trim();
          if (trimmedAuthor) {
            authorSet.add(trimmedAuthor);
          }
        });
      }
    });
    
    const sortedAuthors = Array.from(authorSet).sort((a, b) => a.localeCompare(b));
    return ['All', ...sortedAuthors];
  }, [predictions]);

  // Get year range from all predictions
  const availableYearRange = useMemo(() => {
    if (predictions.length === 0) return { min: 2020, max: 2030 };
    
    const years = predictions.map(p => p.display_year).filter(year => year && !isNaN(year));
    const dataMin = Math.min(...years);
    const dataMax = Math.max(...years);
    
    return {
      min: Math.floor(dataMin / 10) * 10,
      max: Math.ceil(dataMax / 10) * 10
    };
  }, [predictions]);

  // Initialize year range when predictions load
  useEffect(() => {
    if (predictions.length > 0 && yearRange.min === null) {
      setYearRange(availableYearRange);
    }
  }, [predictions, availableYearRange, yearRange.min]);

  // Filter predictions based on selected tags and authors
  const filteredPredictions = useMemo(() => {
    return predictions.filter(prediction => {
      const predictionTags = parseTagsOrAuthors(prediction.tags);
      const tagMatch = selectedTags.includes('All') || 
        selectedTags.some(selectedTag => predictionTags.includes(selectedTag));
      
      const predictionAuthors = parseTagsOrAuthors(prediction.author);
      const authorMatch = selectedAuthors.includes('All') || 
        selectedAuthors.some(selectedAuthor => predictionAuthors.includes(selectedAuthor));
      
      const predictionYear = prediction.display_year;
      const yearMatch = yearRange.min === null || predictionYear >= yearRange.min;
      const yearMatch2 = yearRange.max === null || predictionYear <= yearRange.max;

      return tagMatch && authorMatch && yearMatch && yearMatch2;
    });
  }, [predictions, selectedTags, selectedAuthors, yearRange]);

  const handleFilterChange = (filters) => {
    setSelectedTags(filters);
    posthog?.capture(TRACKING_EVENTS.FILTER_CHANGED, { 
      tags: filters,
      filter_count: filters.length 
    });
  };

  const handleAuthorFilterChange = (authors) => {
    setSelectedAuthors(authors);
    posthog?.capture(TRACKING_EVENTS.AUTHOR_FILTER_CHANGED, { 
      authors: authors,
      author_count: authors.length 
    });
  };

  const handleYearRangeChange = (range) => {
    if (range.min === null && range.max === null) {
      setYearRange(availableYearRange);
      posthog?.capture(TRACKING_EVENTS.YEAR_FILTER_RESET);
    } else {
      setYearRange(range);
      posthog?.capture(TRACKING_EVENTS.YEAR_FILTER_CHANGED, { 
        min_year: range.min,
        max_year: range.max,
        range_span: range.max - range.min 
      });
    }
  };

  const handlePredictionClick = (prediction) => {
    setSelectedPrediction(prediction);
    posthog?.capture(TRACKING_EVENTS.PREDICTION_CLICKED, { 
      prediction_id: prediction.id,
      prediction_year: prediction.display_year,
      prediction_title: prediction.title,
      prediction_author: prediction.author 
    });
  };

  const handleCloseModal = () => {
    setSelectedPrediction(null);
    posthog?.capture(TRACKING_EVENTS.PREDICTION_MODAL_CLOSED);
  };

  return (
    <TimelineContainer theme={themeData}>
      <DatasetToggle 
        isPrivate={isPrivateDataset} 
        onToggle={() => setIsPrivateDataset(!isPrivateDataset)} 
      />
      
      <MainContent>
        <Header>
          <Title theme={themeData}>Singularity Countdown</Title>
          <Subtitle theme={themeData}>
            Expert Predictions Leading to the Singularity
          </Subtitle>
        </Header>

        <FilterSection 
          tags={allTags}
          authors={allAuthors}
          activeFilters={selectedTags}
          activeAuthors={selectedAuthors}
          onFilterChange={handleFilterChange}
          onAuthorFilterChange={handleAuthorFilterChange}
          onYearRangeChange={handleYearRangeChange}
          yearRange={yearRange}
          availableYearRange={availableYearRange}
        />
        
        {error ? (
          <ErrorMessage theme={themeData}>
            Error loading predictions: {error}
          </ErrorMessage>
        ) : isLoading ? (
          <TimelineLoadingContainer theme={themeData}>
            <LoadingSpinner theme={themeData} />
            <p>Loading predictions...</p>
          </TimelineLoadingContainer>
        ) : (
          <VerticalTimeline 
            predictions={filteredPredictions}
            onPredictionClick={handlePredictionClick}
          />
        )}
      </MainContent>

      {selectedPrediction && (
        <PredictionModal 
          prediction={selectedPrediction}
          onClose={handleCloseModal}
          showDebugInfo={isPrivateDataset}
        />
      )}
    </TimelineContainer>
  );
}

export default TimelinePage;
